import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  makeStyles,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Tooltip,
} from "@material-ui/core";
import {
  SyncAlt as SyncAltIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Autorenew as AutorenewIcon,
  DeleteOutline as DeleteOutlineIcon,
  CropFree as QrCodeIcon,
  Settings as SettingsIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
} from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTimezone } from "../../hooks/useTimezone";
import DeleteWhatsAppModal from "../../components/DeleteWhatsAppModal";

const POLL_MS = 30000;

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f8fafc",
    color: theme.palette.text.primary,
    padding: theme.spacing(1.75, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px 8px 0 0",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  metricUnit: {
    fontSize: "14px",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  progressBar: {
    marginTop: theme.spacing(1),
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.mode === "dark" ? "#374151" : "#e5e7eb",
    "& .MuiLinearProgress-bar": {
      borderRadius: 3,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  table: {
    minWidth: 650,
    [theme.breakpoints.down("sm")]: {
      minWidth: 960,
    },
  },
  tableContainer: {
    backgroundColor: "transparent",
    boxShadow: "none",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f8fafc",
    },
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1.4fr) repeat(3, minmax(160px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  diagnosisText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  actionGroup: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  compactButton: {
    minWidth: 116,
  },
  staleRow: {
    backgroundColor: theme.mode === "dark" ? "rgba(127,29,29,0.18)" : "rgba(254,226,226,0.55)",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  kpiCard: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  kpiTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  kpiNumber: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.2,
    marginTop: theme.spacing(0.75),
  },
  kpiHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  tableHeadCell: {
    fontSize: 12,
    fontWeight: 800,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f8fafc",
  },
}));

const getStatusColor = (percentage) => {
  if (percentage < 50) return "#10B981";
  if (percentage < 80) return "#F59E0B";
  if (percentage < 95) return "#EF4444";
  return "#DC2626";
};

const getOperationalStatusLabel = (status) => {
  switch (status) {
    case "healthy":
      return "Operativa";
    case "reconnecting":
      return "Reconectando";
    case "pairing":
      return "Emparejando";
    case "offline":
      return "Desconectada";
    case "attention":
      return "Atención";
    default:
      return "Sin estado";
  }
};

const getOperationalStatusColor = (status) => {
  switch (status) {
    case "healthy":
      return "#10B981";
    case "reconnecting":
      return "#3B82F6";
    case "pairing":
      return "#8B5CF6";
    case "offline":
      return "#6B7280";
    case "attention":
      return "#F59E0B";
    default:
      return "#6B7280";
  }
};

const CHANNEL_INFO = {
  whatsapp: { label: "WhatsApp", color: "#25D366", Icon: WhatsAppIcon },
  telegram: { label: "Telegram", color: "#229ED9", Icon: TelegramIcon },
  telegram_bot: { label: "Bot de la tienda (Telegram)", color: "#229ED9", Icon: TelegramIcon },
};
const channelInfo = (connection) => CHANNEL_INFO[connection.channel] || CHANNEL_INFO.whatsapp;
const isTelegramChannel = (connection) => String(connection.channel || "").startsWith("telegram");

const getConnectionPriority = (connection) => {
  if (connection.operationalStatus === "offline") return 0;
  if (connection.operationalStatus === "attention") return 1;
  if (connection.operationalStatus === "pairing") return 2;
  if (connection.operationalStatus === "reconnecting") return 3;
  return 4;
};

const matchesStatusFilter = (connection, statusFilter) => {
  if (statusFilter === "all") return true;
  if (statusFilter === "actionable") {
    return connection.operationalStatus === "offline" || connection.operationalStatus === "attention";
  }
  return connection.operationalStatus === statusFilter;
};

const PAIRING_REASONS = [
  "USER_DISCONNECT",
  "PHONE_DISCONNECT",
  "USER_DELETE",
  "PAIRING_TIMEOUT",
  "REPAIR_REQUIRED",
  "BAD_SESSION",
  "BAD_SESSION_CLEANUP_FAILED",
  "MANUAL_CLEANUP",
];

const needsPairing = (connection) =>
  connection.operationalStatus === "pairing" ||
  PAIRING_REASONS.includes(connection.lastDisconnectReason);

const getPrimaryAction = (connection, userCompanyId) => {
  if (connection.managed === "negocio") {
    return Number(connection.companyId) === Number(userCompanyId)
      ? { type: "configure", label: "Configurar" }
      : { type: "none" };
  }
  if (connection.operationalStatus === "reconnecting") {
    return { type: "busy", label: "Reconexión en curso" };
  }
  if (connection.lastDisconnectReason === "PLAN_LIMIT_REACHED") {
    return { type: "blocked", label: "Límite de plan" };
  }
  if (needsPairing(connection)) {
    if (Number(connection.companyId) === Number(userCompanyId)) {
      return { type: "pair", label: "Vincular" };
    }
    return { type: "blocked", label: "Requiere QR en la empresa" };
  }
  if (connection.operationalStatus === "offline" || connection.operationalStatus === "attention") {
    return { type: "reconnect", label: "Reconectar" };
  }
  return { type: "none" };
};

const ConnectionsDashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isSuper = Boolean(user?.super || user?.profile === "super");
  const history = useHistory();
  const { formatDateTime } = useTimezone();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionFilter, setConnectionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("actionable");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [connectionsPage, setConnectionsPage] = useState(0);
  const [connectionsRowsPerPage, setConnectionsRowsPerPage] = useState(10);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [refreshing, setRefreshing] = useState(false);

  const loadDashboardData = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setRefreshing(true);
        setError(null);
      }
      const response = await api.get("/dashboard/connections");

      if (response.data?.success && response.data?.data) {
        setDashboardData(response.data.data);
      }
    } catch (err) {
      console.error("Error cargando dashboard:", err);
      if (!silent) {
        setError("Error al cargar datos del dashboard");
        toast.error("Error al cargar datos del dashboard");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const runReconnect = async () => {
    const connection = actionDialog?.connection;
    if (!connection || actionDialog?.action !== "reconnect") return;
    const loadingKey = `reconnect-${connection.id}`;

    try {
      setActionLoading((prev) => ({ ...prev, [loadingKey]: true }));
      await api.post(`/whatsapp/${connection.id}/restart`);
      toast.success(`Reconexión iniciada para ${connection.name}`);
      setActionDialog(null);
      await loadDashboardData();
    } catch (err) {
      const backendError = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(backendError || `No se pudo reconectar ${connection.name}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleCompleteDelete = async (whatsAppId) => {
    try {
      const response = await api.delete(`/whatsapp/${whatsAppId}`);
      if (response.status === 200 && response.data?.deletedStats) {
        const stats = response.data.deletedStats;
        toast.success(
          `Eliminada: ${stats.deletedTickets} tickets, ${stats.deletedContacts} contactos, ${stats.deletedMessages} mensajes`
        );
      } else {
        toast.success("Conexión eliminada");
      }
      return response.data;
    } catch (err) {
      const backendError = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(backendError || "No se pudo eliminar la conexión");
      throw err;
    }
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => loadDashboardData({ silent: true }), POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const { global, byCompany = [], connections = [] } = dashboardData || {};
  const companyOptions = useMemo(
    () => [...new Map(connections.map((item) => [item.companyId, item])).values()],
    [connections]
  );
  const filteredConnections = useMemo(
    () =>
      connections
        .filter((connection) => {
          const searchValue = connectionFilter.trim().toLowerCase();
          const matchesSearch =
            !searchValue ||
            [connection.companyName, connection.name, connection.number, connection.diagnosis].some(
              (value) =>
                String(value || "")
                  .toLowerCase()
                  .includes(searchValue)
            );
          const matchesCompany =
            companyFilter === "all" || String(connection.companyId) === String(companyFilter);
          const matchesChannel =
            channelFilter === "all" || (channelFilter === "telegram") === isTelegramChannel(connection);
          return (
            matchesSearch && matchesStatusFilter(connection, statusFilter) && matchesCompany && matchesChannel
          );
        })
        .sort((a, b) => getConnectionPriority(a) - getConnectionPriority(b)),
    [connections, connectionFilter, statusFilter, companyFilter, channelFilter]
  );
  const paginatedConnections = useMemo(() => {
    const start = connectionsPage * connectionsRowsPerPage;
    return filteredConnections.slice(start, start + connectionsRowsPerPage);
  }, [filteredConnections, connectionsPage, connectionsRowsPerPage]);
  const connectionStats = useMemo(() => {
    const total = connections.length;
    const offline = connections.filter((item) => item.operationalStatus === "offline").length;
    const attention = connections.filter((item) => item.operationalStatus === "attention").length;
    const reconnecting = connections.filter(
      (item) => item.operationalStatus === "reconnecting" || item.operationalStatus === "pairing"
    ).length;
    const healthy = connections.filter((item) => item.operationalStatus === "healthy").length;
    const telegram = connections.filter(isTelegramChannel);
    return {
      telegramTotal: telegram.length,
      telegramHealthy: telegram.filter((item) => item.operationalStatus === "healthy").length,
      total,
      healthy,
      offline,
      attention,
      reconnecting,
      actionable: offline + attention,
    };
  }, [connections]);
  const utilization = Number(global?.utilization || 0);
  const capacityLabel =
    utilization < 50 ? "Capacidad estable" : utilization < 80 ? "Cerca del límite" : "Riesgo operativo";

  useEffect(() => {
    setConnectionsPage(0);
  }, [connectionFilter, statusFilter, companyFilter, channelFilter]);

  if (loading && !dashboardData) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          Cargando dashboard de conexiones...
        </Typography>
      </Box>
    );
  }

  if (error && !dashboardData) {
    return (
      <Box className={classes.loadingContainer}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => loadDashboardData()} style={{ marginTop: 16 }}>
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6">No hay datos disponibles</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography variant="h5" style={{ fontWeight: 800 }}>
            Control de conexiones
          </Typography>
          <Typography variant="body2" color="textSecondary">
            WhatsApp y Telegram: lo que requiere acción, diagnóstico y cupo de plan
            {isSuper ? " de todas las empresas." : " de tu empresa."}
          </Typography>
        </Box>
        <Box className={classes.headerActions}>
          <Button
            size="small"
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => loadDashboardData()}
            variant="contained"
            color="primary"
            disabled={refreshing}
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      <Box className={classes.kpiGrid}>
        <Box className={classes.kpiCard}>
          <Typography className={classes.kpiTitle}>Requieren acción</Typography>
          <Typography
            className={classes.kpiNumber}
            style={{ color: connectionStats.actionable > 0 ? "#B91C1C" : "#047857" }}
          >
            {connectionStats.actionable}
          </Typography>
          <Typography className={classes.kpiHint}>
            Desconectadas o en atención
            {connectionStats.reconnecting > 0 ? ` · ${connectionStats.reconnecting} reconectando` : ""}
          </Typography>
        </Box>
        <Box className={classes.kpiCard}>
          <Typography className={classes.kpiTitle}>Operativas</Typography>
          <Typography className={classes.kpiNumber}>
            {connectionStats.healthy}
            <span className={classes.metricUnit}> / {connectionStats.total}</span>
          </Typography>
          <Typography className={classes.kpiHint}>
            WhatsApp {connectionStats.healthy - connectionStats.telegramHealthy}/
            {connectionStats.total - connectionStats.telegramTotal} · Telegram {connectionStats.telegramHealthy}/
            {connectionStats.telegramTotal}
          </Typography>
        </Box>
        <Box className={classes.kpiCard}>
          <Typography className={classes.kpiTitle}>Cupo del plan</Typography>
          <Typography className={classes.kpiNumber} style={{ color: getStatusColor(utilization) }}>
            {utilization.toFixed(1)}
            <span className={classes.metricUnit}>%</span>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(utilization, 100)}
            className={classes.progressBar}
            style={{ marginTop: 8 }}
          />
          <Typography className={classes.kpiHint}>
            {isSuper
              ? `${capacityLabel} · todos los planes`
              : `${capacityLabel} · límite de tu plan`}
          </Typography>
        </Box>
      </Box>

      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <SyncAltIcon />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Conexiones
          </Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <Box
            className={classes.filterRow}
            style={isSuper ? undefined : { gridTemplateColumns: "minmax(220px, 1.4fr) repeat(2, minmax(160px, 1fr))" }}
          >
            <TextField
              label="Buscar conexión o diagnóstico"
              value={connectionFilter}
              onChange={(e) => setConnectionFilter(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="actionable">Requieren acción</MenuItem>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="healthy">Operativa</MenuItem>
              <MenuItem value="attention">Atención</MenuItem>
              <MenuItem value="reconnecting">Reconectando</MenuItem>
              <MenuItem value="pairing">Emparejando</MenuItem>
              <MenuItem value="offline">Desconectada</MenuItem>
            </TextField>
            <TextField
              select
              label="Canal"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="telegram">Telegram</MenuItem>
            </TextField>
            {isSuper && (
              <TextField
                select
                label="Empresa"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              >
                <MenuItem value="all">Todas</MenuItem>
                {companyOptions.map((company) => (
                  <MenuItem key={company.companyId} value={String(company.companyId)}>
                    {company.companyName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  {isSuper && <TableCell className={classes.tableHeadCell}>Empresa</TableCell>}
                  <TableCell className={classes.tableHeadCell}>Conexión</TableCell>
                  <TableCell className={classes.tableHeadCell}>Estado</TableCell>
                  <TableCell className={classes.tableHeadCell}>Diagnóstico</TableCell>
                  <TableCell className={classes.tableHeadCell}>Actividad</TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedConnections.map((connection) => {
                  const reconnectKey = `reconnect-${connection.id}`;
                  const activityAt = connection.lastActivity || connection.updatedAt;
                  const primaryAction = getPrimaryAction(connection, user?.companyId);
                  const channel = channelInfo(connection);
                  return (
                    <TableRow
                      key={connection.id}
                      className={`${classes.tableRow} ${getConnectionPriority(connection) <= 1 ? classes.staleRow : ""}`}
                    >
                      {isSuper && (
                        <TableCell>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {connection.companyName}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <Tooltip title={channel.label}>
                            <channel.Icon style={{ color: channel.color, fontSize: 20 }} />
                          </Tooltip>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {connection.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {connection.number ||
                                (isTelegramChannel(connection) ? "Bot sin usuario" : "Sin número vinculado")}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getOperationalStatusLabel(connection.operationalStatus)}
                          size="small"
                          style={{
                            backgroundColor: getOperationalStatusColor(connection.operationalStatus),
                            color: "white",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{connection.diagnosis}</Typography>
                        {connection.operationalStatus !== "healthy" && connection.lastDisconnectReason && (
                          <Typography className={classes.diagnosisText}>
                            {connection.lastDisconnectReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {activityAt ? formatDateTime(activityAt) : "Sin registro"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box className={classes.actionGroup}>
                          {primaryAction.type === "reconnect" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={
                                actionLoading[reconnectKey] ? <CircularProgress size={14} /> : <AutorenewIcon />
                              }
                              className={classes.compactButton}
                              disabled={Boolean(actionLoading[reconnectKey] || deleteTarget)}
                              onClick={() =>
                                setActionDialog({
                                  action: "reconnect",
                                  connection,
                                  title: "Reconectar conexión",
                                  description: `Se reiniciará la sesión de ${connection.name}${
                                    isSuper ? ` en ${connection.companyName}` : ""
                                  } sin pedir QR.`,
                                })
                              }
                            >
                              Reconectar
                            </Button>
                          )}
                          {primaryAction.type === "pair" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<QrCodeIcon />}
                              className={classes.compactButton}
                              onClick={() =>
                                history.push(`/connections?whatsappId=${connection.id}&pair=1`)
                              }
                            >
                              Vincular
                            </Button>
                          )}
                          {primaryAction.type === "configure" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<SettingsIcon />}
                              className={classes.compactButton}
                              onClick={() => history.push("/negocio/configuracion")}
                            >
                              Configurar
                            </Button>
                          )}
                          {(primaryAction.type === "busy" || primaryAction.type === "blocked") && (
                            <Tooltip title={primaryAction.label}>
                              <Typography variant="caption" color="textSecondary">
                                {primaryAction.label}
                              </Typography>
                            </Tooltip>
                          )}
                          {connection.managed !== "negocio" && (
                            <Button
                              size="small"
                              variant="outlined"
                              style={{ borderColor: "#DC2626", color: "#DC2626" }}
                              startIcon={<DeleteOutlineIcon />}
                              className={classes.compactButton}
                              disabled={Boolean(actionLoading[reconnectKey] || deleteTarget)}
                              onClick={() => setDeleteTarget(connection)}
                            >
                              Eliminar
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredConnections.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuper ? 6 : 5} align="center">
                      <Typography variant="body2" color="textSecondary" style={{ padding: "18px 0" }}>
                        {statusFilter === "actionable"
                          ? "No hay conexiones que requieran acción."
                          : "No hay conexiones que coincidan con los filtros aplicados."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredConnections.length}
            page={connectionsPage}
            onPageChange={(event, newPage) => setConnectionsPage(newPage)}
            rowsPerPage={connectionsRowsPerPage}
            onRowsPerPageChange={(event) => {
              setConnectionsRowsPerPage(parseInt(event.target.value, 10));
              setConnectionsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </CardContent>
      </Card>

      {isSuper && byCompany.length > 0 && (
        <Card className={classes.card}>
          <Box className={classes.cardHeader}>
            <BusinessIcon />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Capacidad por empresa
            </Typography>
          </Box>
          <CardContent className={classes.cardContent}>
            <TableContainer component={Paper} style={{ backgroundColor: "transparent", boxShadow: "none" }}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Empresa</TableCell>
                    <TableCell className={classes.tableHeadCell}>Plan</TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">
                      Operativas
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">
                      Límite
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">
                      Uso
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byCompany.map((company) => (
                    <TableRow key={company.companyId} className={classes.tableRow}>
                      <TableCell>{company.companyName}</TableCell>
                      <TableCell>{company.planName}</TableCell>
                      <TableCell align="right">{company.activeSessions}</TableCell>
                      <TableCell align="right">{company.planLimit}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gridGap={8}>
                          <Box width={90}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(company.utilization, 100)}
                              className={classes.progressBar}
                            />
                          </Box>
                          <Typography style={{ color: getStatusColor(company.utilization), fontWeight: 700 }}>
                            {company.utilization.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={Boolean(actionDialog)}
        onClose={() => setActionDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{actionDialog?.title || "Confirmar acción"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{actionDialog?.description}</Typography>
          {actionDialog?.connection && (
            <Box marginTop={2}>
              {isSuper && (
                <Typography variant="caption" color="textSecondary" display="block">
                  Empresa: {actionDialog.connection.companyName}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary" display="block">
                Estado: {getOperationalStatusLabel(actionDialog.connection.operationalStatus)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancelar</Button>
          <Button
            onClick={runReconnect}
            color="primary"
            variant="contained"
            disabled={Boolean(
              actionDialog?.connection && actionLoading[`reconnect-${actionDialog.connection.id}`]
            )}
          >
            {actionDialog?.connection && actionLoading[`reconnect-${actionDialog.connection.id}`]
              ? "Procesando..."
              : "Reconectar"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteWhatsAppModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        whatsAppName={deleteTarget?.name || "WhatsApp"}
        whatsAppId={deleteTarget?.id}
        onConfirm={handleCompleteDelete}
        onDeleteComplete={() => {
          setDeleteTarget(null);
          loadDashboardData();
        }}
      />

      <Box textAlign="center" marginTop={2}>
        <Typography variant="caption" color="textSecondary">
          Auto-actualización cada 30 segundos
        </Typography>
      </Box>
    </Box>
  );
};

export default ConnectionsDashboard;
